import React, { useState, useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import VerticalBar from "@/components/icons/VerticalBar";
import ChevronRight from "@/components/icons/ChevronRight";
import CircleArrowRight from "@/components/icons/CircleArrowRight";
import MyStatics from "@/components/home/MyStatics";
import TodayProblem from "@/components/home/TodayProblem";
import AICard from "@/components/home/AICard";
import SolvedProblems from "@/components/home/SolvedProblems";
import Carousel from "@/components/home/Carousel";
import useGetProblemPreview from "@/fetcher/useGetProblemPreview";
import { useGetEssayPreview } from "@/fetcher/essay-learning";
import { useGetNotices, BoardItem } from "@/fetcher/home/useGetNotices";
import formatDate from "@/utils/my/formatDate";
import { useRouter } from "next/router";
import { useGetBannerMain, BannerItem } from "@/fetcher/home/useGetBannerMain";
import ReturnToTestModal from "@/components/modal/ReturnToTestModal";
import { fetchProblems } from "pages/api/problem/fetchProblems";
import { useRecoilState } from "recoil";
import {
  problemDataState,
  unansweredQuestionsState,
  tempDataState,
  retryState,
  answerDataState,
  isAnswerSubmittedState,
  currentQuestionIndexState,
  problemPauseState,
  currentChapterIdState,
} from "@/recoil/atom";
import { Question } from "@/types/problemProps";
import { processSuspendedAnswers } from "@/utils/problem/processSuspendedAnswers";

/**
 * 로그인한 학생의 홈.
 *
 * accessToken 이 있을 때만 마운트된다. 비로그인 방문자에게는 랜딩 페이지를 보여주고,
 * 로그인 사용자 전용 훅과 fetch 가 비로그인 방문자에게 실행되지 않도록 분리한 컴포넌트다.
 *
 * 화면 위에서 아래로 배치 순서:
 *  1. MyStatics (인사말 + 학습통계)
 *  2. 나의 역량 리포트 진입 카드 (체험용 계정도 유일하게 실제 이동이 되는 자리. /my?view=report)
 *  3. TodayProblem(오늘의 객관식·서술형) + 오른쪽 열에 AICard, SolvedProblems
 *  4. Carousel (배너)
 *  5. 공지사항 목록
 *  6. 고객센터 안내 / 계좌 안내 2칸
 */
export default function StudentHome() {
  const router = useRouter();
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [bannerList, setBannerList] = useState<BannerItem[]>([
    {
      bannerId: 0,
      imageUrl: "/images/testBanner.avif",
      url: "https://haebom.ai.kr",
    },
  ]);
  const [boardList, setBoardList] = useState<BoardItem[]>([]);

  // Recoil states needed for session restoration
  const [, setProblemData] = useRecoilState(problemDataState);
  const [, setIsPaused] = useRecoilState(problemPauseState);
  const [, setUnansweredQuestions] = useRecoilState(unansweredQuestionsState);
  const [, setTempDataList] = useRecoilState(tempDataState);
  const [, setAnswerList] = useRecoilState(answerDataState);
  const [, setIsRetry] = useRecoilState(retryState);
  const [, setIsAnswerSubmitted] = useRecoilState(isAnswerSubmittedState);
  const [, setCurrentQuestionIndex] = useRecoilState(currentQuestionIndexState);

  const [, setCurrentChapterId] = useRecoilState(currentChapterIdState);

  // 객관식·서술형 두 preview를 마운트 시점에 동시 호출. 카드 한 줄 요약과 모달 매트릭스에 모두 필요.
  // 모달이 떠야 호출되는 패턴은 깜빡임 유발 → 절대 금지.
  const { data: previewData } = useGetProblemPreview();
  const { data: essayPreview } = useGetEssayPreview();

  // 배너 목록 가져오기
  const getBannerList = async () => {
    const response = await useGetBannerMain();
    if (response && response.isSuccess) {
      setBannerList(response.result);
    } else {
      console.error("배너 목록을 가져오는 데 실패했습니다.");
    }
  };

  // 공지사항 목록 가져오기
  const getBoardList = async () => {
    const response = await useGetNotices();
    if (response && response.isSuccess) {
      setBoardList(response.result);
    } else {
      console.error("공지사항 목록을 가져오는 데 실패했습니다.");
    }
  };

  const handleBoardClick = (boardId: number) => {
    router.push({
      pathname: "/help/notice",
      query: { boardId },
    });
  };

  // suspendedAnswer가 있는 문제들을 tempDataList에 담는 로직
  const processSuspendedtoTemps = (datas: Question[]) => {
    return datas
      .filter((item) => item.suspendedAnswer !== null)
      .map((item) => ({
        questionId: item.suspendedAnswer!.questionId,
        answer: item.suspendedAnswer!.answer,
        answerSecond: item.suspendedAnswer!.answerSecond,
      }));
  };

  // 문제 데이터 로드 및 세션 복구 준비
  const checkSuspendedProblem = async () => {
    try {
      const res = await fetchProblems();
      if (Array.isArray(res)) {
        const hasSuspendedAnswers = res.some(
          (item: Question) => item.suspendedAnswer !== null
        );

        if (hasSuspendedAnswers) {
          // 상태 미리 세팅 (복귀 시 바로 쓸 수 있게)
          setProblemData(res);
          setCurrentChapterId(res[0].chapterId); // 챕터 ID 저장
          setIsPaused(false);
          setIsAnswerSubmitted(false);

          const initialUnansweredQuestions = res.map((_: any, index: number) => index);
          const initialTempDataList = processSuspendedtoTemps(res);
          setTempDataList(initialTempDataList);

          const answeredQuestionIds = initialTempDataList
            .filter((tempData) => tempData.answer !== "")
            .map((tempData) => tempData.questionId);

          const filteredUnansweredQuestions = initialUnansweredQuestions.filter(
            (index: number) => !answeredQuestionIds.includes(res[index].questionId)
          );

          if (res.length - filteredUnansweredQuestions.length > 0) setIsRetry(true);
          setUnansweredQuestions(filteredUnansweredQuestions);
          setCurrentQuestionIndex(filteredUnansweredQuestions[0] ?? 0);

          const arr = processSuspendedAnswers(res);
          setAnswerList(arr);

          // 모달 노출
          setIsReturnModalOpen(true);
        }
      }
    } catch (err) {
      console.error("Failed to check suspended problems:", err);
    }
  };

  useEffect(() => {
    getBannerList();
    getBoardList();
  }, []);

  // 임시저장 데이터 체크 (세션당 한 번만 수행)
  useEffect(() => {
    const alreadyChecked = sessionStorage.getItem("suspended_checked");
    if (!alreadyChecked) {
      checkSuspendedProblem();
      sessionStorage.setItem("suspended_checked", "true");
    }
  }, []);

  return (
    <>
      <Header />
      <section className="max-w-[1300px] mx-auto flex flex-col gap-y-[80px] px-[81px] pt-[40px] pb-[80px] bg-white">
        {/* 상태바 */}
        <MyStatics isTodaySolved={!!previewData?.completedLastChapterDate} />

        {/* 체험용 계정 전용, 리포트만 직접 접근 가능한 경로 */}
        <article
          onClick={() => router.push({ pathname: "/my", query: { view: "report" } })}
          className="cursor-pointer rounded-[20px] bg-primary-soft p-[24px] flex justify-between items-center"
        >
          <div className="flex flex-col gap-y-[8px]">
            <div className="text-20 font-bold text-heading">나의 역량 리포트</div>
            <div className="text-16 text-body font-normal">
              AI가 분석한 나의 학습 리포트를 확인해요
            </div>
          </div>
          <CircleArrowRight />
        </article>

        <div className="flex gap-x-[20px]">
          <TodayProblem previewData={previewData} essayPreview={essayPreview} />
          <div className="w-1/2 flex flex-col gap-y-[20px]">
            <AICard />
            <SolvedProblems />
          </div>
        </div>

        {/* 캐러셀 */}
        <Carousel bannerList={bannerList} />

        {/* 공지사항 */}
        <article className="flex flex-col">
          <div className="flex justify-between">
            <div className="text-notice">공지사항</div>
            <div
              onClick={() => router.push("/help/notice")}
              className="flex items-center"
            >
              <div className="text-14 text-strong font-m cursor-pointer">
                더보기
              </div>
              <div className="ml-1">
                <ChevronRight />
              </div>
            </div>
          </div>

          <div className="mt-[25px] flex flex-col rounded-[20px] overflow-hidden border border-line-subtle">
            <div className="notice-title">
              <div className="notice-content title-text py-2">
                <div>제목 </div>
                <div className="pr-[75px]">작성일</div>
              </div>
            </div>
            {boardList.map((notice, index) => (
              <div
                onClick={() => handleBoardClick(notice.boardId)}
                key={index}
                className="notice-content py-2"
              >
                <div className="text-15 text-body max-w-[500px] text-container cursor-pointer">
                  {notice.title}
                </div>
                <div className="text-14 text-body font-normal mr-10">
                  {formatDate(notice.createdAt)}
                </div>
              </div>
            ))}
            {!boardList && (
              <div className="mt-[10px]  pl-[15px] h-[35px]">
                <span className="text-15 font-normal">
                  등록된 공지사항이 없습니다.
                </span>
              </div>
            )}
          </div>
        </article>

        {/* 카드 */}
        <article className="flex gap-[24px]">
          {/* 2026-09-06 사용자 결정: 의미 토큰(bg-page=바탕)이 아니라 색 자체로 gray-50 을 쓴다 — 홈 흰 섹션 위의 연한 회색 타일. */}
          <div className="w-6/12 bg-gray-50 rounded-[20px] p-[24px]">
            <div className="text-20 font-bold  text-heading ">고객센터</div>
            <div className="text-blue-md pt-[10px]">02-313-8910</div>

            <div className="text-14 text-heading font-bold pt-[24px]">
              근무시간
            </div>

            <div className="flex pt-[10px] items-center">
              <p className="text-time text-body pr-1">평일</p>
              <p className="text-time text-strong">09:00 - 18:00</p>
              <div className="px-[10px]">
                <VerticalBar />
              </div>

              <p className="text-time text-body pr-1">점심시간 </p>
              <p className="text-time text-strong"> 12:00 - 13:00</p>
              <div className="px-[10px]">
                <VerticalBar />
              </div>

              <p className="text-time text-strong">주말 공휴일 휴무</p>
            </div>
          </div>

          <div className="w-6/12 bg-primary-soft rounded-[20px] p-[24px] flex flex-col justify-center items-center gap-[10px]">
            <div className="text-20 font-bold text-heading">계좌 안내</div>
            <div className="text-blue-lg ">100-035-987061</div>

            <div className="flex">
              <div className="text-20 font-bold text-heading">신한은행</div>
              <div className="px-[10px]">
                <VerticalBar />
              </div>
              <div className="text-20 font-bold text-heading">
                예금주: (주)코드하나
              </div>
            </div>
          </div>
        </article>
      </section>

      <Footer />

      <ReturnToTestModal
        isOpen={isReturnModalOpen}
        onClose={() => setIsReturnModalOpen(false)}
        onReturn={() => {
          router.push("/problem");
        }}
      />
    </>
  );
}
